import './settings/settings-leaf.css';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { locationApi, type LocationUpdateBody, type LocationWithIntakeLink } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { usePlanFeature } from './plan-gate.tsx';
import { Alert, Button, LoadingBlock, Select, TextArea, TextInput } from './ui.tsx';
import { MoneyInput } from './money-input.tsx';
import { NumberInput } from './number-input.tsx';
import { timezoneOptions } from '../forms/timezones.ts';
import { currencyOptions } from '../forms/currencies.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { formatLocalTime } from '../i18n/datetime.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { parseWorkDays } from '@shared/scheduling-hours.ts';
import {
  BILLING_INCREMENTS,
  PLANS,
  SMS_SENDER_LABEL_MAX,
  minimumPlanFor,
  planHasFeature,
} from '@shared/constants/index.ts';
import type { Company, Location } from '@shared/types/index.ts';

/**
 * Locations (Settings → Locations).
 *
 * Every company has at least one location from signup; more than one is a
 * Business-plan capability. Each location may override a handful of company
 * settings — everything else it inherits — and the whole point of this UI is
 * that inherit-or-override stays visible and reversible: an empty field means
 * "the company value" (shown right there), a filled one means "this location's
 * own", and clearing it goes back to inheriting.
 *
 * Archiving locks a location without deleting anything in it; a downgrade off
 * Business does the same automatically, and re-upgrading restores.
 */

/** Overrides for on/off settings need a third state: inherit. */
type TriState = 'inherit' | 'on' | 'off';

interface OverrideDraft {
  name: string;
  email: string;
  phone: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  /** '' = inherit. */
  timezone: string;
  /** '' = inherit the company's currency. */
  currencyCode: string;
  defaultHourlyRateCents: number | null;
  defaultQuoteExpirationDays: number | null;
  taxEnabled: TriState;
  defaultTaxRateBasisPoints: number | null;
  laborIsTaxable: TriState;
  tipsEnabled: TriState;
  lateCancelFeeEnabled: TriState;
  lateCancelNoticeHours: number | null;
  lateCancelFeeCents: number | null;
  autoOfferCancelledSlots: TriState;
  cancelledSlotNoticeHours: number | null;
  cancelledSlotRoundHours: number | null;
  cancelledSlotRoundSize: number | null;
  cancelledSlotQuietStartHour: number | null;
  cancelledSlotQuietEndHour: number | null;
  cancelledSlotRequireApproval: TriState;
  /** '' = inherit, otherwise the minutes as a string for the <select>. */
  billingIncrementMinutes: string;
  quoteNumberPrefix: string;
  invoiceNumberPrefix: string;
  defaultTerms: string;
  appointmentConfirmLeadHours: number | null;
  appointmentReminderLeadHours: number | null;
  smsSenderLabel: string;
  /** '' = inherit, otherwise 'HH:MM'. */
  workDayStartLocal: string;
  workDayEndLocal: string;
  /** '' = inherit, otherwise the comma list the API stores. */
  workDays: string;
}

function triFrom(value: boolean | null): TriState {
  return value === null ? 'inherit' : value ? 'on' : 'off';
}

function triTo(value: TriState): boolean | null {
  return value === 'inherit' ? null : value === 'on';
}

function draftFrom(location: Location): OverrideDraft {
  const o = location.overrides;
  return {
    name: location.name,
    email: o.email ?? '',
    phone: o.phone ?? '',
    website: o.website ?? '',
    addressLine1: o.addressLine1 ?? '',
    addressLine2: o.addressLine2 ?? '',
    city: o.city ?? '',
    state: o.state ?? '',
    postalCode: o.postalCode ?? '',
    timezone: o.timezone ?? '',
    currencyCode: o.currencyCode ?? '',
    defaultHourlyRateCents: o.defaultHourlyRateCents,
    defaultQuoteExpirationDays: o.defaultQuoteExpirationDays,
    taxEnabled: triFrom(o.taxEnabled),
    defaultTaxRateBasisPoints: o.defaultTaxRateBasisPoints,
    laborIsTaxable: triFrom(o.laborIsTaxable),
    tipsEnabled: triFrom(o.tipsEnabled),
    lateCancelFeeEnabled: triFrom(o.lateCancelFeeEnabled),
    lateCancelNoticeHours: o.lateCancelNoticeHours,
    lateCancelFeeCents: o.lateCancelFeeCents,
    autoOfferCancelledSlots: triFrom(o.autoOfferCancelledSlots),
    cancelledSlotNoticeHours: o.cancelledSlotNoticeHours,
    cancelledSlotRoundHours: o.cancelledSlotRoundHours,
    cancelledSlotRoundSize: o.cancelledSlotRoundSize,
    cancelledSlotQuietStartHour: o.cancelledSlotQuietStartHour,
    cancelledSlotQuietEndHour: o.cancelledSlotQuietEndHour,
    cancelledSlotRequireApproval: triFrom(o.cancelledSlotRequireApproval),
    billingIncrementMinutes: o.billingIncrementMinutes === null ? '' : String(o.billingIncrementMinutes),
    quoteNumberPrefix: o.quoteNumberPrefix ?? '',
    invoiceNumberPrefix: o.invoiceNumberPrefix ?? '',
    defaultTerms: o.defaultTerms ?? '',
    appointmentConfirmLeadHours: o.appointmentConfirmLeadHours,
    appointmentReminderLeadHours: o.appointmentReminderLeadHours,
    smsSenderLabel: o.smsSenderLabel ?? '',
    workDayStartLocal: o.workDayStartLocal ?? '',
    workDayEndLocal: o.workDayEndLocal ?? '',
    workDays: o.workDays ?? '',
  };
}

/** Empty text means "inherit", which the API spells `null`. */
function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function payloadFrom(draft: OverrideDraft): LocationUpdateBody {
  return {
    name: draft.name.trim(),
    email: textOrNull(draft.email),
    phone: textOrNull(draft.phone),
    website: textOrNull(draft.website),
    addressLine1: textOrNull(draft.addressLine1),
    addressLine2: textOrNull(draft.addressLine2),
    city: textOrNull(draft.city),
    state: textOrNull(draft.state),
    postalCode: textOrNull(draft.postalCode),
    timezone: textOrNull(draft.timezone),
    currencyCode: textOrNull(draft.currencyCode),
    defaultHourlyRateCents: draft.defaultHourlyRateCents,
    defaultQuoteExpirationDays: draft.defaultQuoteExpirationDays,
    taxEnabled: triTo(draft.taxEnabled),
    defaultTaxRateBasisPoints: draft.defaultTaxRateBasisPoints,
    laborIsTaxable: triTo(draft.laborIsTaxable),
    tipsEnabled: triTo(draft.tipsEnabled),
    lateCancelFeeEnabled: triTo(draft.lateCancelFeeEnabled),
    lateCancelNoticeHours: draft.lateCancelNoticeHours,
    lateCancelFeeCents: draft.lateCancelFeeCents,
    autoOfferCancelledSlots: triTo(draft.autoOfferCancelledSlots),
    cancelledSlotNoticeHours: draft.cancelledSlotNoticeHours,
    cancelledSlotRoundHours: draft.cancelledSlotRoundHours,
    cancelledSlotRoundSize: draft.cancelledSlotRoundSize,
    cancelledSlotQuietStartHour: draft.cancelledSlotQuietStartHour,
    cancelledSlotQuietEndHour: draft.cancelledSlotQuietEndHour,
    cancelledSlotRequireApproval: triTo(draft.cancelledSlotRequireApproval),
    billingIncrementMinutes: draft.billingIncrementMinutes === '' ? null : Number(draft.billingIncrementMinutes),
    quoteNumberPrefix: textOrNull(draft.quoteNumberPrefix),
    invoiceNumberPrefix: textOrNull(draft.invoiceNumberPrefix),
    defaultTerms: textOrNull(draft.defaultTerms),
    appointmentConfirmLeadHours: draft.appointmentConfirmLeadHours,
    appointmentReminderLeadHours: draft.appointmentReminderLeadHours,
    smsSenderLabel: textOrNull(draft.smsSenderLabel),
    workDayStartLocal: textOrNull(draft.workDayStartLocal),
    workDayEndLocal: textOrNull(draft.workDayEndLocal),
    workDays: textOrNull(draft.workDays),
  };
}

export default function LocationsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { company, can, refresh } = useSession();

  const canManage = can('company:write');
  const locations = useQuery({ queryKey: ['locations'], queryFn: () => locationApi.list() });

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // The list in the header's switcher and the list here must agree, and a
  // rename/archive changes both — so every mutation refreshes the session too.
  async function afterMutation(text: string | null) {
    setError(null);
    setNotice(text);
    await queryClient.invalidateQueries({ queryKey: ['locations'] });
    // The roster too: adding a branch can clear an owner's personal hours
    // that no longer resolve against it (see healInvertedWorkingWindows), and
    // a Team section still showing the old ones would offer to save a window
    // the server has just refused.
    await queryClient.invalidateQueries({ queryKey: ['company-users'] });
    // And the payment status: a location's tips override changes the
    // effective switch the record-payment forms render their tip field from.
    await queryClient.invalidateQueries({ queryKey: ['payments-status'] });
    await refresh();
  }

  function onFailure(caught: unknown, fallback: string) {
    setNotice(null);
    setError(caught instanceof ApiRequestError ? caught.message : fallback);
  }

  const create = useMutation({
    mutationFn: (name: string) => locationApi.create({ name }),
    onSuccess: async () => {
      setAdding(false);
      setNewName('');
      await afterMutation(t('locations.addedMessage'));
    },
    onError: (caught) => onFailure(caught, t('locations.addError')),
  });

  if (!company) return null;

  const hasMultiLocation = planHasFeature(company.effectivePlan, 'multi_location');
  const trialing = company.billingStatus === 'trialing';
  const requiredPlanName = PLANS[minimumPlanFor('multi_location')].name;

  function handleAdd(event: FormEvent) {
    event.preventDefault();
    if (newName.trim() === '') return;
    create.mutate(newName.trim());
  }

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="locations-heading">
      <div className="work-section-heading">
        <h2 id="locations-heading" className="text-lg font-semibold">
          {t('locations.heading')}
        </h2>
        {canManage && hasMultiLocation && !adding ? (
          <Button onClick={() => setAdding(true)}>{t('locations.addLocation')}</Button>
        ) : null}
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('locations.description')}</p>

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {notice ? (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      ) : null}

      {canManage && !hasMultiLocation ? (
        <div className="mb-4">
          <Alert tone="info" title={t('locations.upgradeTitle')}>
            <p>{t('locations.upgradeNote', { plan: requiredPlanName })}</p>
            <a href="#settings-billing" className="mt-2 inline-block font-semibold hover:underline">
              {t('locations.upgradeCta')}
            </a>
          </Alert>
        </div>
      ) : null}

      {canManage && hasMultiLocation && trialing ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">
          {t('locations.trialNote', { plan: requiredPlanName })}
        </p>
      ) : null}

      {adding ? (
        <form onSubmit={handleAdd} noValidate className="mb-6 rounded-md border border-[color:var(--color-line)] p-4">
          <TextInput
            label={t('locations.nameLabel')}
            required
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
          <div className="flex gap-3">
            <Button type="submit" variant="primary" busy={create.isPending}>
              {t('locations.addLocation')}
            </Button>
            <Button onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
          </div>
        </form>
      ) : null}

      {locations.isLoading ? <LoadingBlock label={t('common.loading')} /> : null}

      <ul className="divide-y divide-[color:var(--color-line)]">
        {(locations.data?.locations ?? []).map((location) => (
          <LocationRow
            key={location.id}
            location={location}
            company={company}
            canManage={canManage}
            expanded={expandedId === location.id}
            onToggle={() => setExpandedId((current) => (current === location.id ? null : location.id))}
            onChanged={afterMutation}
            onFailed={onFailure}
          />
        ))}
      </ul>
    </section>
  );
}

function LocationRow({
  location,
  company,
  canManage,
  expanded,
  onToggle,
  onChanged,
  onFailed,
}: {
  location: LocationWithIntakeLink;
  company: Company;
  canManage: boolean;
  expanded: boolean;
  onToggle: () => void;
  onChanged: (text: string | null) => Promise<void>;
  onFailed: (caught: unknown, fallback: string) => void;
}) {
  const { t } = useTranslation();

  const archive = useMutation({
    mutationFn: () => locationApi.archive(location.id),
    onSuccess: () => void onChanged(t('locations.archivedMessage', { name: location.name })),
    onError: (caught) => onFailed(caught, t('locations.archiveError')),
  });
  const restore = useMutation({
    mutationFn: () => locationApi.restore(location.id),
    onSuccess: () => void onChanged(t('locations.restoredMessage', { name: location.name })),
    onError: (caught) => onFailed(caught, t('locations.restoreError')),
  });

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="truncate font-medium">{location.name}</p>
          {location.isPrimary ? (
            <span className="badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">
              {t('locations.primaryBadge')}
            </span>
          ) : null}
          {location.status === 'archived' ? (
            <span className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
              {location.archivedReason === 'downgrade'
                ? t('locations.archivedDowngradeBadge')
                : t('locations.archivedBadge')}
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canManage && location.status === 'archived' ? (
            <Button busy={restore.isPending} onClick={() => restore.mutate()}>
              {t('locations.restore')}
            </Button>
          ) : null}
          {canManage && location.status === 'active' && !location.isPrimary ? (
            <Button
              variant="danger"
              busy={archive.isPending}
              onClick={() => {
                if (window.confirm(t('locations.archiveConfirm', { name: location.name }))) archive.mutate();
              }}
            >
              {t('locations.archive')}
            </Button>
          ) : null}
          <Button onClick={onToggle} aria-expanded={expanded}>
            {expanded ? t('locations.collapse') : t('locations.expand')}
          </Button>
        </div>
      </div>

      {location.status === 'archived' && location.archivedReason === 'downgrade' ? (
        <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('locations.archivedDowngradeNote')}</p>
      ) : null}

      {expanded ? (
        <LocationEditor location={location} company={company} canManage={canManage} onChanged={onChanged} />
      ) : null}
    </li>
  );
}

function LocationEditor({
  location,
  company,
  canManage,
  onChanged,
}: {
  location: LocationWithIntakeLink;
  company: Company;
  canManage: boolean;
  onChanged: (text: string | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  // Earlier-visit offers: every paid plan, never Free.
  const offersEarlierVisits = usePlanFeature('earlier_visit_offers');
  const locale = getDateLocale();

  const [draft, setDraft] = useState<OverrideDraft>(() => draftFrom(location));
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Usage counts come from the detail endpoint; the list already carries the
  // overrides, so only the counts wait on this query.
  const detail = useQuery({
    queryKey: ['location', location.id],
    queryFn: () => locationApi.get(location.id),
  });

  const save = useMutation({
    mutationFn: () => locationApi.update(location.id, payloadFrom(draft)),
    onSuccess: async () => {
      setError(null);
      setFieldErrors({});
      await onChanged(t('locations.savedMessage', { name: draft.name.trim() || location.name }));
      await detail.refetch();
    },
    onError: (caught) => {
      if (caught instanceof ApiRequestError) {
        setFieldErrors(caught.fields);
        setError(Object.keys(caught.fields).length > 0 ? null : caught.message);
      } else {
        setError(t('locations.saveError'));
      }
    },
  });

  function set<K extends keyof OverrideDraft>(key: K, value: OverrideDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  // A branch in its own currency prices only from its own amounts: the
  // company's are numbers in another currency, so they are not offered as
  // what blank inherits, and the Worker refuses a rate left blank.
  const ownCurrency = draft.currencyCode !== '';
  const currency = draft.currencyCode || company.currencyCode;

  /** "Company setting: X" — every override field says what blank inherits. */
  const inherited = (value: string | null | undefined): string =>
    t('locations.inheritedValue', { value: value === null || value === undefined || value === '' ? '—' : value });

  /**
   * The same hint for the two hour fields, whose value arrives as the wire's
   * '17:00' rather than as anything a reader should be shown.
   */
  const inheritedTime = (value: string | null | undefined): string => inherited(value ? formatLocalTime(value) : value);

  const onOff = (value: boolean) => (value ? t('locations.on') : t('locations.off'));
  const triOptions = (companyValue: boolean) => [
    { value: 'inherit', label: t('locations.inheritOption', { value: onOff(companyValue) }) },
    { value: 'on', label: t('locations.on') },
    { value: 'off', label: t('locations.off') },
  ];

  const usage = detail.data?.usage;

  return (
    <div className="mt-3 rounded-md border border-[color:var(--color-line)] p-4">
      {usage ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">
          {t('locations.usageLine', {
            customers: usage.customers,
            quotes: usage.quotes,
            appointments: usage.appointments,
          })}
        </p>
      ) : detail.isLoading ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('common.loading')}</p>
      ) : null}

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <TextInput
        label={t('locations.nameLabel')}
        required
        value={draft.name}
        disabled={!canManage}
        error={fieldErrors.name}
        onChange={(event) => set('name', event.target.value)}
      />

      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('locations.overridesHint')}</p>

      <OverrideGroup title={t('locations.groupContact')}>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <TextInput
            label={t('settings.email')}
            type="email"
            value={draft.email}
            placeholder={company.email ?? ''}
            hint={inherited(company.email)}
            disabled={!canManage}
            error={fieldErrors.email}
            onChange={(event) => set('email', event.target.value)}
          />
          <TextInput
            label={t('settings.phone')}
            type="tel"
            value={draft.phone}
            placeholder={company.phone ?? ''}
            hint={inherited(company.phone)}
            disabled={!canManage}
            error={fieldErrors.phone}
            onChange={(event) => set('phone', event.target.value)}
          />
        </div>
        <TextInput
          label={t('settings.website')}
          type="url"
          value={draft.website}
          placeholder={company.website ?? 'https://'}
          hint={inherited(company.website)}
          disabled={!canManage}
          error={fieldErrors.website}
          onChange={(event) => set('website', event.target.value)}
        />
        <TextInput
          label={t('settings.addressLine1')}
          value={draft.addressLine1}
          placeholder={company.addressLine1 ?? ''}
          hint={inherited(company.addressLine1)}
          disabled={!canManage}
          onChange={(event) => set('addressLine1', event.target.value)}
        />
        <TextInput
          label={t('settings.addressLine2')}
          value={draft.addressLine2}
          placeholder={company.addressLine2 ?? ''}
          hint={inherited(company.addressLine2)}
          disabled={!canManage}
          onChange={(event) => set('addressLine2', event.target.value)}
        />
        <div className="grid gap-x-4 sm:grid-cols-3">
          <TextInput
            label={t('settings.city')}
            value={draft.city}
            placeholder={company.city ?? ''}
            hint={inherited(company.city)}
            disabled={!canManage}
            onChange={(event) => set('city', event.target.value)}
          />
          <TextInput
            label={t('settings.state')}
            value={draft.state}
            placeholder={company.state ?? ''}
            hint={inherited(company.state)}
            disabled={!canManage}
            onChange={(event) => set('state', event.target.value)}
          />
          <TextInput
            label={t('settings.postalCode')}
            value={draft.postalCode}
            placeholder={company.postalCode ?? ''}
            hint={inherited(company.postalCode)}
            disabled={!canManage}
            onChange={(event) => set('postalCode', event.target.value)}
          />
        </div>
        <Select
          label={t('settings.timezone')}
          value={draft.timezone}
          hint={inherited(company.timezone)}
          disabled={!canManage}
          options={[
            { value: '', label: t('locations.inheritOption', { value: company.timezone }) },
            ...timezoneOptions(draft.timezone || company.timezone),
          ]}
          onChange={(event) => set('timezone', event.target.value)}
        />
      </OverrideGroup>

      <OverrideGroup title={t('locations.groupPricing')}>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <Select
            label={t('locations.currency')}
            value={draft.currencyCode}
            hint={t('locations.currencyHint')}
            disabled={!canManage}
            error={fieldErrors.currencyCode}
            options={[
              { value: '', label: t('locations.inheritOption', { value: company.currencyCode }) },
              ...currencyOptions(draft.currencyCode || company.currencyCode),
            ]}
            onChange={(event) => set('currencyCode', event.target.value)}
          />
          <MoneyInput
            currencyCode={currency}
            label={t('settings.hourlyRate')}
            valueCents={draft.defaultHourlyRateCents}
            hint={
              ownCurrency
                ? t('locations.ownCurrencyAmountHint', { currency })
                : inherited(formatCents(company.defaultHourlyRateCents, company.currencyCode, locale))
            }
            error={fieldErrors.defaultHourlyRateCents}
            disabled={!canManage}
            min={0}
            onChangeCents={(cents) => set('defaultHourlyRateCents', cents)}
          />
          <Select
            label={t('settings.roundUpTo')}
            value={draft.billingIncrementMinutes}
            hint={inherited(t(`schedule.billingIncrement.${company.billingIncrementMinutes}`))}
            disabled={!canManage}
            options={[
              {
                value: '',
                label: t('locations.inheritOption', {
                  value: t(`schedule.billingIncrement.${company.billingIncrementMinutes}`),
                }),
              },
              ...BILLING_INCREMENTS.map((increment) => ({
                value: String(increment.minutes),
                label: t(`schedule.billingIncrement.${increment.minutes}`),
              })),
            ]}
            onChange={(event) => set('billingIncrementMinutes', event.target.value)}
          />
          <NumberInput
            label={t('settings.expirationDays')}
            min={1}
            max={365}
            value={draft.defaultQuoteExpirationDays}
            placeholder={String(company.defaultQuoteExpirationDays)}
            hint={inherited(String(company.defaultQuoteExpirationDays))}
            disabled={!canManage}
            error={fieldErrors.defaultQuoteExpirationDays}
            onValueChange={(days) => set('defaultQuoteExpirationDays', days)}
          />
          <TextInput
            label={t('settings.quoteNumberPrefix')}
            value={draft.quoteNumberPrefix}
            placeholder={company.quoteNumberPrefix}
            hint={inherited(company.quoteNumberPrefix)}
            disabled={!canManage}
            error={fieldErrors.quoteNumberPrefix}
            onChange={(event) => set('quoteNumberPrefix', event.target.value)}
          />
          <TextInput
            label={t('settings.invoiceNumberPrefix')}
            value={draft.invoiceNumberPrefix}
            placeholder={company.invoiceNumberPrefix}
            hint={inherited(company.invoiceNumberPrefix)}
            disabled={!canManage}
            error={fieldErrors.invoiceNumberPrefix}
            onChange={(event) => set('invoiceNumberPrefix', event.target.value)}
          />
        </div>

        <div className="grid gap-x-4 sm:grid-cols-2">
          <Select
            label={t('settings.chargeTax')}
            value={draft.taxEnabled}
            disabled={!canManage}
            options={triOptions(company.taxEnabled)}
            onChange={(event) => set('taxEnabled', event.target.value as TriState)}
          />
          <Select
            label={t('settings.laborTaxable')}
            value={draft.laborIsTaxable}
            disabled={!canManage}
            options={triOptions(company.laborIsTaxable)}
            onChange={(event) => set('laborIsTaxable', event.target.value as TriState)}
          />
          <Select
            label={t('settings.tipsEnable')}
            hint={t('locations.tipsHint')}
            value={draft.tipsEnabled}
            disabled={!canManage}
            options={triOptions(company.tipsEnabled)}
            onChange={(event) => set('tipsEnabled', event.target.value as TriState)}
          />
          <NumberInput
            label={t('settings.taxRate')}
            min={0}
            max={100}
            step={0.001}
            value={draft.defaultTaxRateBasisPoints === null ? null : draft.defaultTaxRateBasisPoints / 100}
            placeholder={String(company.defaultTaxRateBasisPoints / 100)}
            hint={inherited(`${company.defaultTaxRateBasisPoints / 100}%`)}
            disabled={!canManage}
            error={fieldErrors.defaultTaxRateBasisPoints}
            onValueChange={(percent) =>
              set('defaultTaxRateBasisPoints', percent === null ? null : Math.max(0, Math.round(percent * 100)))
            }
          />
        </div>

        <TextArea
          label={t('settings.defaultTerms')}
          rows={4}
          value={draft.defaultTerms}
          placeholder={company.defaultTerms ?? ''}
          hint={t('locations.termsInheritHint')}
          disabled={!canManage}
          onChange={(event) => set('defaultTerms', event.target.value)}
        />
      </OverrideGroup>

      <OverrideGroup title={t('locations.groupScheduling')}>
        <div className="grid gap-x-4 sm:grid-cols-3">
          <NumberInput
            label={t('settings.confirmLeadHours')}
            min={1}
            max={336}
            value={draft.appointmentConfirmLeadHours}
            placeholder={String(company.appointmentConfirmLeadHours)}
            hint={inherited(String(company.appointmentConfirmLeadHours))}
            disabled={!canManage}
            error={fieldErrors.appointmentConfirmLeadHours}
            onValueChange={(hours) => set('appointmentConfirmLeadHours', hours)}
          />
          <NumberInput
            label={t('settings.reminderLeadHours')}
            min={1}
            max={336}
            value={draft.appointmentReminderLeadHours}
            placeholder={String(company.appointmentReminderLeadHours)}
            hint={inherited(String(company.appointmentReminderLeadHours))}
            disabled={!canManage}
            error={fieldErrors.appointmentReminderLeadHours}
            onValueChange={(hours) => set('appointmentReminderLeadHours', hours)}
          />
          <Select
            label={t('settings.lateCancelEnable')}
            hint={t('locations.lateCancelHint')}
            value={draft.lateCancelFeeEnabled}
            disabled={!canManage}
            options={triOptions(company.lateCancelFeeEnabled)}
            onChange={(event) => set('lateCancelFeeEnabled', event.target.value as TriState)}
          />
          <NumberInput
            label={t('settings.lateCancelNoticeHours')}
            min={1}
            max={336}
            value={draft.lateCancelNoticeHours}
            placeholder={String(company.lateCancelNoticeHours)}
            hint={inherited(String(company.lateCancelNoticeHours))}
            disabled={!canManage}
            error={fieldErrors.lateCancelNoticeHours}
            onValueChange={(hours) => set('lateCancelNoticeHours', hours)}
          />
          <MoneyInput
            currencyCode={currency}
            label={t('settings.lateCancelFee')}
            valueCents={draft.lateCancelFeeCents}
            hint={
              ownCurrency
                ? t('locations.ownCurrencyAmountHint', { currency })
                : inherited(formatCents(company.lateCancelFeeCents, company.currencyCode, locale))
            }
            disabled={!canManage}
            min={0}
            onChangeCents={(cents) => set('lateCancelFeeCents', cents)}
          />
          <Select
            label={t('settings.fillSlotsEnable')}
            hint={offersEarlierVisits ? t('locations.fillSlotsHint') : t('settings.fillSlotsLocked')}
            value={draft.autoOfferCancelledSlots}
            disabled={!canManage || !offersEarlierVisits}
            options={triOptions(company.autoOfferCancelledSlots)}
            onChange={(event) => set('autoOfferCancelledSlots', event.target.value as TriState)}
          />
          <NumberInput
            label={t('settings.fillSlotsNoticeHours')}
            min={1}
            max={336}
            value={draft.cancelledSlotNoticeHours}
            placeholder={String(company.cancelledSlotNoticeHours)}
            hint={inherited(String(company.cancelledSlotNoticeHours))}
            disabled={!canManage}
            error={fieldErrors.cancelledSlotNoticeHours}
            onValueChange={(hours) => set('cancelledSlotNoticeHours', hours)}
          />
          <NumberInput
            label={t('settings.fillSlotsRoundSize')}
            min={1}
            max={20}
            value={draft.cancelledSlotRoundSize}
            placeholder={String(company.cancelledSlotRoundSize)}
            hint={inherited(String(company.cancelledSlotRoundSize))}
            disabled={!canManage}
            error={fieldErrors.cancelledSlotRoundSize}
            onValueChange={(count) => set('cancelledSlotRoundSize', count)}
          />
          <NumberInput
            label={t('settings.fillSlotsRoundHours')}
            min={1}
            max={72}
            value={draft.cancelledSlotRoundHours}
            placeholder={String(company.cancelledSlotRoundHours)}
            hint={inherited(String(company.cancelledSlotRoundHours))}
            disabled={!canManage}
            error={fieldErrors.cancelledSlotRoundHours}
            onValueChange={(hours) => set('cancelledSlotRoundHours', hours)}
          />
          <NumberInput
            label={t('settings.fillSlotsQuietStart')}
            min={0}
            max={23}
            value={draft.cancelledSlotQuietStartHour}
            placeholder={String(company.cancelledSlotQuietStartHour)}
            hint={inherited(String(company.cancelledSlotQuietStartHour))}
            disabled={!canManage}
            error={fieldErrors.cancelledSlotQuietStartHour}
            onValueChange={(hour) => set('cancelledSlotQuietStartHour', hour)}
          />
          <NumberInput
            label={t('settings.fillSlotsQuietEnd')}
            min={0}
            max={23}
            value={draft.cancelledSlotQuietEndHour}
            placeholder={String(company.cancelledSlotQuietEndHour)}
            hint={inherited(String(company.cancelledSlotQuietEndHour))}
            disabled={!canManage}
            error={fieldErrors.cancelledSlotQuietEndHour}
            onValueChange={(hour) => set('cancelledSlotQuietEndHour', hour)}
          />
          <Select
            label={t('settings.fillSlotsApproval')}
            hint={t('locations.fillSlotsApprovalHint')}
            value={draft.cancelledSlotRequireApproval}
            disabled={!canManage}
            options={triOptions(company.cancelledSlotRequireApproval)}
            onChange={(event) => set('cancelledSlotRequireApproval', event.target.value as TriState)}
          />
          <TextInput
            label={t('settings.smsSenderName')}
            maxLength={SMS_SENDER_LABEL_MAX}
            value={draft.smsSenderLabel}
            placeholder={company.smsSenderLabel ?? company.name}
            hint={inherited(company.smsSenderLabel ?? company.name)}
            disabled={!canManage}
            error={fieldErrors.smsSenderLabel}
            onChange={(event) => set('smsSenderLabel', event.target.value)}
          />
        </div>

        {/* This branch's own hours. Empty is not "closed" — it is "whatever
            head office keeps", which is why the company's values sit in the
            placeholders rather than being copied into the fields. */}
        <div className="grid gap-x-4 sm:grid-cols-2">
          <TextInput
            label={t('settings.workDayStart')}
            type="time"
            step={300}
            value={draft.workDayStartLocal}
            placeholder={company.workDayStartLocal}
            hint={inheritedTime(company.workDayStartLocal)}
            disabled={!canManage}
            error={fieldErrors.workDayStartLocal}
            onChange={(event) => set('workDayStartLocal', event.target.value)}
          />
          <TextInput
            label={t('settings.workDayEnd')}
            type="time"
            step={300}
            value={draft.workDayEndLocal}
            placeholder={company.workDayEndLocal}
            hint={inheritedTime(company.workDayEndLocal)}
            disabled={!canManage}
            error={fieldErrors.workDayEndLocal}
            onChange={(event) => set('workDayEndLocal', event.target.value)}
          />
        </div>
        <fieldset className="mb-3">
          <legend className="field-label">{t('settings.workDaysLabel')}</legend>
          <div className="flex flex-wrap gap-2">
            {[0, 1, 2, 3, 4, 5, 6].map((day) => {
              // While inheriting, the company's days are what this branch
              // actually works — so they are what the buttons show, and the
              // first click starts the override from them rather than from
              // an empty week nobody chose.
              const inheriting = draft.workDays === '';
              const shown = parseWorkDays(inheriting ? company.workDays : draft.workDays);
              const active = shown.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  role="checkbox"
                  aria-checked={active}
                  disabled={!canManage}
                  onClick={() => {
                    const next = active ? shown.filter((d) => d !== day) : [...shown, day].sort((a, b) => a - b);
                    if (next.length > 0) set('workDays', next.join(','));
                  }}
                  className={
                    'rounded-full border px-3 py-1.5 text-sm font-medium disabled:opacity-60 ' +
                    (active
                      ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                      : 'border-[color:var(--color-line)] text-[color:var(--color-muted)]') +
                    (inheriting ? ' opacity-70' : '')
                  }
                >
                  {t(`schedule.dayOfWeek.${day}`)}
                </button>
              );
            })}
          </div>
          <p className="field-hint mt-2">
            {draft.workDays === '' ? t('locations.workDaysInheriting') : t('locations.workDaysOverridden')}
          </p>
          {draft.workDays === '' || !canManage ? null : (
            <button
              type="button"
              className="mt-1 text-sm text-[color:var(--color-brand)] hover:underline"
              onClick={() => set('workDays', '')}
            >
              {t('locations.workDaysFollowCompany')}
            </button>
          )}
        </fieldset>
      </OverrideGroup>

      {canManage ? (
        <div className="mt-4 flex gap-3">
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            {t('locations.save')}
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() => {
              setDraft(draftFrom(location));
              setFieldErrors({});
              setError(null);
            }}
          >
            {t('settings.discard')}
          </Button>
        </div>
      ) : null}

      {location.status === 'active' ? (
        <LocationIntakeLink location={location} canManage={canManage} onChanged={onChanged} />
      ) : null}
    </div>
  );
}

/**
 * A location's own public quote-request link, mirroring the company-wide one
 * in Settings → Web form & widget: requests filed through this address land
 * in this location rather than the primary one. Like the company link it is
 * public by design and re-displayable, creating again is deliberately a
 * rotation (the old address dies), and removing it leaves the company-wide
 * form working untouched.
 */
function LocationIntakeLink({
  location,
  canManage,
  onChanged,
}: {
  location: LocationWithIntakeLink;
  canManage: boolean;
  onChanged: (text: string | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const url = location.intakePath ? `${window.location.origin}${location.intakePath}` : null;

  function onFailure(caught: unknown) {
    setError(caught instanceof ApiRequestError ? caught.message : t('locations.intakeError'));
  }

  const create = useMutation({
    mutationFn: () => locationApi.createIntakeLink(location.id),
    onSuccess: () => {
      setError(null);
      // The prop still holds the pre-mutation value, so it distinguishes a
      // first mint from a rotation.
      void onChanged(
        location.intakePath
          ? t('locations.intakeRotatedMessage', { name: location.name })
          : t('locations.intakeCreatedMessage', { name: location.name }),
      );
    },
    onError: onFailure,
  });

  const remove = useMutation({
    mutationFn: () => locationApi.removeIntakeLink(location.id),
    onSuccess: () => {
      setError(null);
      void onChanged(t('locations.intakeRemovedMessage', { name: location.name }));
    },
    onError: onFailure,
  });

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('locations.intakeCopyError'));
    }
  }

  return (
    <div className="mt-4 border-t border-[color:var(--color-line)] pt-4">
      <h3 className="mb-1 text-sm font-semibold">{t('locations.intakeHeading')}</h3>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('locations.intakeDescription')}</p>

      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {url ? (
        <div className="mb-3">
          <p className="field-label">{t('locations.intakeLinkLabel')}</p>
          <p className="break-all font-mono text-sm">{url}</p>
        </div>
      ) : (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('locations.intakeOff')}</p>
      )}

      <div className="flex flex-wrap gap-2">
        {url ? (
          <>
            <Button onClick={() => void copy()}>{copied ? t('common.copied') : t('common.copyLink')}</Button>
            {canManage ? (
              <>
                <Button
                  busy={create.isPending}
                  onClick={() => {
                    if (window.confirm(t('locations.intakeConfirmRotate'))) create.mutate();
                  }}
                >
                  {t('locations.intakeRotate')}
                </Button>
                <Button
                  variant="danger"
                  busy={remove.isPending}
                  onClick={() => {
                    if (window.confirm(t('locations.intakeConfirmRemove'))) remove.mutate();
                  }}
                >
                  {t('locations.intakeRemove')}
                </Button>
              </>
            ) : null}
          </>
        ) : canManage ? (
          <Button variant="primary" busy={create.isPending} onClick={() => create.mutate()}>
            {t('locations.intakeCreate')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function OverrideGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-4 border-t border-[color:var(--color-line)] pt-4">
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      {children}
    </div>
  );
}
